import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Button, message, Form, Tabs, Card, Row, Col } from 'antd';
import {
  FileTextOutlined,
  PayCircleOutlined,
  PlusCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';

import {
  addCarHouseSubscription,
  editCarHouseSubscription,
  cancelSubscriptionAction,
  fetchCarHouseSubscriptions,
  editSubscription,
} from '../store/actions/subscriptionsAndCredits-actions';
import Spinner from '../components/UI/Spinner/Spinner';
import { getCarHouseInvoicesItems } from '../lib/invoices';

import {
  fetchCarHouseInvoices,
  payCarHouseInvoices,
  fetchCarHouseInvoicesItems,
} from '../store/actions/invoices-actions';

import { getCarHouseById } from '../lib/carHouses';

import { addCarHouseCredits } from '../lib/subscriptionsAndCredits';

import CreditModal from '../components/Subscriptions/CreditModal';
import SubscriptionModal from '../components/Subscriptions/SubscriptionModal';
import SubscriptionTable from '../components/Subscriptions/SubscriptionTable';
import InvoiceItemsModal from '../components/Subscriptions/InvoiceItemsModal';
import PayInvoiceModal from '../components/Subscriptions/PayInvoiceModal';
import PaymentDetailsModal from '../components/Subscriptions/PaymentDetailsModal';
import InvoicesTable from '../components/Subscriptions/InvoiceTable';

const { TabPane } = Tabs;

const SubscriptionsPage = () => {
  const { t } = useTranslation();
  const loggedInUser = useSelector((state) => state.auth.loggedInUser);

  const { subscriptionsLoading, subscriptions } = useSelector(
    (state) => state.subscriptionsAndCredits
  );

  const { invoices, invoiceItems, itemsLoading } = useSelector(
    (state) => state.invoices
  );

  const { carHouseId, userId } = useParams();

  const dispatch = useDispatch();
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [subscriptionForm] = Form.useForm();
  const [creditForm] = Form.useForm();
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const [openPayModal, setOpenPayModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openItemsModal, setOpenItemsModal] = useState(false);

  const [subPerPage, setSubPerPage] = useState(8);
  const [subPage, setSubPage] = useState(1);
  const [currentItemsPage, setItemsCurrentPage] = useState(1);
  const itemsPerPage = 4;

  const [activeTab, setActiveTab] = useState('1');

  const [openCreditModal, setOpenCreditModal] = useState(false);
  const [carHouse, setCarHouse] = useState(null);

  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState(false);
  const [payments, setPayments] = useState([]);

  const [activeSubscription, setActiveSubscription] = useState(null);
  const [isEditSubscription, setIsEditSubscription] = useState(false);

  useEffect(() => {
    dispatch(
      fetchCarHouseSubscriptions({
        carHouseId: carHouseId,
        pagination: { page: subPage, total: subPerPage },
      })
    );
    dispatch(fetchCarHouseInvoices(carHouseId));
    getCarHouseById(carHouseId).then((res) => {
      setCarHouse(res.data);
    });
  }, [dispatch, carHouseId, userId, subPage, subPerPage]);

  useEffect(() => {
    if (subscriptions.subscriptions?.length) {
      const active = subscriptions.subscriptions.find(
        (sub) => sub.status === 'active'
      );
      setActiveSubscription(active || null);
    }
  }, [subscriptions]);

  // Handle Tab change to prevent reset
  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const showAddButton =
    !subscriptions.subscriptions?.some(
      (sub) => sub.status === 'active' || sub.status === 'pending'
    ) && loggedInUser.role.name === 'root';

  const showChangeButton =
    subscriptions.subscriptions?.some(
      (sub) => sub.status === 'active' || sub.status === 'pending'
    ) && loggedInUser.role.name === 'root';

  const showAddCreditButton =
    subscriptions.subscriptions?.some(
      (sub) => sub.status === 'active' && sub.paymentType === 'prepaid'
    ) && loggedInUser.role.name === 'root';

  const handleChangeSubscription = async () => {
    if (!activeSubscription) {
      message.warning('No active subscription to change.');
      return;
    }

    setSelectedSubscription(activeSubscription);
    setOpenSubscriptionModal(true);
    setIsEditSubscription(false);

    subscriptionForm.setFieldsValue({
      startTime: moment(activeSubscription.startTime).format(
        'YYYY-MM-DDTHH:mm'
      ),
      endTime: moment(activeSubscription.endTime).format('YYYY-MM-DDTHH:mm'),
      paymentType: activeSubscription.paymentType,
    });
  };

  const handleEditSubscription = async (subscription) => {
    setSelectedSubscription(activeSubscription);
    setOpenSubscriptionModal(true);
    setIsEditSubscription(true);

    subscriptionForm.setFieldsValue({
      startTime: moment(activeSubscription.startTime).format(
        'YYYY-MM-DDTHH:mm'
      ),
      endTime: moment(activeSubscription.endTime).format('YYYY-MM-DDTHH:mm'),
      paymentType: activeSubscription.paymentType,
    });
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      await dispatch(cancelSubscriptionAction(subscriptionId));
      message.success(t('subscriptions.subscriptionCancelSuccess'));
    } catch (error) {
      message.error(t('subscriptions.subscriptionCancelFail'));
    }
  };

  const handlePayInvoice = async (paymentAmount) => {
    const body = {
      amount: paymentAmount,
    };

    try {
      let response;

      response = await dispatch(
        payCarHouseInvoices({
          carHouseId: carHouseId,
          invoiceId: selectedInvoice.id,
          body,
        })
      );

      if (response?.payload?.errorCode) {
        message.error(
          response?.payload?.description ||
            'An error occurred while processing the payment'
        );
      } else {
        message.success(t('subscriptions.paymentSuccess'));
        setOpenPayModal(false);
        dispatch(fetchCarHouseInvoices(carHouseId));
      }
    } catch (error) {
      message.error(t('subscriptions.paymentFail'));
    }
  };

  const handleInvoiceDetails = (invoice) => {
    setItemsCurrentPage(1)
    setSelectedInvoice(invoice);
    dispatch(
      fetchCarHouseInvoicesItems({
        carHouseId: carHouseId,
        invoiceId: invoice.id,
        pagination: { page: 1, total: itemsPerPage },
      })
    );
    setOpenItemsModal(true);
  };

  const handleInvoiceItemsPageChange = (page) => {
    setItemsCurrentPage(page);
    dispatch(
      fetchCarHouseInvoicesItems({
        carHouseId: carHouseId,
        invoiceId: selectedInvoice.id,
        pagination: { page: page, total: itemsPerPage },
      })
    );
  };

  const onAddCredit = () => {
    setOpenCreditModal(true);
  };

  const onSubmitSubscription = async (values) => {
    let body = values;

    try {
      let resultAction;

      if (isEditSubscription && selectedSubscription) {
        const { paymentType, startTime, ...rest } = values;
        body = rest;

        resultAction = await dispatch(
          editSubscription({
            subscriptionId: selectedSubscription.id,
            body,
          })
        );
      } else if (selectedSubscription) {
        resultAction = await dispatch(
          editCarHouseSubscription({
            carHouseId,
            subscriptionId: selectedSubscription.id,
            body,
          })
        );
      } else {
        resultAction = await dispatch(
          addCarHouseSubscription({
            body,
            carHouseId,
          })
        );
      }

      if (
        editSubscription.fulfilled.match(resultAction) ||
        editCarHouseSubscription.fulfilled.match(resultAction) ||
        addCarHouseSubscription.fulfilled.match(resultAction)
      ) {
        await dispatch(
          fetchCarHouseSubscriptions({
            carHouseId,
            pagination: { page: subPage, total: subPerPage },
          })
        );
        dispatch(fetchCarHouseInvoices(carHouseId));
        setOpenSubscriptionModal(false);
        setSelectedSubscription(null);
        message.success(t('subscriptions.subscriptionSaved'));
      } else {
        const error = resultAction.payload || resultAction.error;
        const errorMessage =
          error?.description ||
          error?.message ||
          (typeof error === 'string' ? error : null) ||
          'Failed to save subscription';

        message.error(errorMessage);
      }
    } catch (err) {
      message.error(t('subscriptions.unexpectedError'));
    }
  };

  const addCreditHandler = async (body) => {
    try {
      addCarHouseCredits(carHouseId, body);
      setCarHouse((prev) => ({
        ...prev,
        credits: (prev?.credits || 0) + body.amount,
      }));
      message.success(t('subscriptions.addCreditSuccess'));

      setOpenCreditModal(false);
    } catch (err) {
      console.log(err);
      setOpenCreditModal(false);
      message.error(t('subscriptions.addCreditFail'));
    }
  };

  const onSubmitCredit = async (values) => {
    let body = values;
    addCreditHandler(body);
    setOpenCreditModal(false);
  };

  const handleSubscriptionPageChange = (page) => {
    setSubPage(page);
  };

  const handleDownloadInvoiceItems = async () => {
    const format = 'csv';
    const data = await getCarHouseInvoicesItems(
      carHouseId,
      selectedInvoice.id,
      { page: currentItemsPage, total: itemsPerPage },
      format
    );
    const csvBlob = new Blob([data], { type: 'text/csv' });
    const url = URL.createObjectURL(csvBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'invoice_items.csv';
    link.click();
  };

  const handlePaymentDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setPayments(invoice.payments);
    setOpenPaymentDetailsModal(true);
  };

  return (
    <>
      <h2>{t('subscriptions.title', { carHouse: carHouse?.name })}</h2>
      {subscriptionsLoading && !openItemsModal ? (
        <Spinner />
      ) : (
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          {/* Tab 1: Subscriptions */}
          <TabPane tab={t('subscriptions.tabs.subscriptions')} key="1">
            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: 16,
                }}
              >
                {showAddButton && (
                  <Button
                    type="primary"
                    shape="round"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      setOpenSubscriptionModal(true);
                      setIsEditSubscription(false);
                    }}
                    style={{ marginBottom: 16 }}
                  >
                    {t('subscriptions.addSubscription')}
                  </Button>
                )}
                {showAddCreditButton && (
                  <>
                    <div>
                      <p style={{ fontSize: '18px' }}>
                        {t('subscriptions.totalCredits')}:
                        <span style={{ fontWeight: 'bold', marginLeft: '5px' }}>
                          {carHouse?.credits}
                        </span>
                      </p>
                    </div>
                    <Button
                      type="primary"
                      shape="round"
                      icon={<PlusCircleOutlined />}
                      onClick={onAddCredit}
                      style={{ marginBottom: 16 }}
                    >
                     {t('subscriptions.addCredits')}
                    </Button>
                  </>
                )}
                {showChangeButton && (
                  <Button
                    type="primary"
                    shape="round"
                    ghost
                    icon={<EditOutlined />}
                    onClick={handleChangeSubscription}
                    style={{ marginBottom: 16 }}
                  >
                    {t('subscriptions.changeSubscription')}
                  </Button>
                )}
              </div>
              {subscriptions.subscriptions?.length ? (
                <SubscriptionTable
                  subscriptions={subscriptions}
                  showAddButton={showAddButton}
                  handleEditSubscription={handleEditSubscription}
                  handleCancelSubscription={handleCancelSubscription}
                  subPage={subPage}
                  subPerPage={subPerPage}
                  handleSubscriptionPageChange={handleSubscriptionPageChange}
                  role={loggedInUser.role.name}
                />
              ) : (
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <h3>{t('subscriptions.noSubscriptions')}</h3>
                </div>
              )}
            </div>
          </TabPane>

          {/* Tab 2: Invoices */}
          <TabPane tab={t('subscriptions.tabs.invoices')} key="2">
            <div>
              <Card title={t('subscriptions.activeInvoice')} variant="borderless">
                <div>
                  {invoices.currentInvoice ? (
                    <div>
                      <p className="m-0">
                        <strong>{t('subscriptions.createdAt')}: </strong>
                        {moment(invoices.currentInvoice.createdAt).format(
                          'MMMM Do, YYYY'
                        )}
                      </p>

                      <p>
                        <strong>{t('subscriptions.type')}:</strong>{' '}
                        {invoices.currentInvoice.subscription?.paymentType.toUpperCase()}
                      </p>

                      <p>
                        <strong>{t('subscriptions.credits')}:</strong>{' '}
                        {invoices.currentInvoice.credits || 0}
                      </p>

                      <Row gutter={8}>
                        {invoices.currentInvoice.subscription?.paymentType ===
                          'postpaid' &&
                          loggedInUser.role.name === 'root' && (
                            <Col>
                              <Button
                                type="primary"
                                ghost
                                icon={<PayCircleOutlined />}
                                onClick={() => {
                                  setSelectedInvoice(invoices.currentInvoice);
                                  setOpenPayModal(true);
                                }}
                              >
                                {t('subscriptions.pay')}
                              </Button>
                            </Col>
                          )}
                        <Col>
                          <Button
                            icon={<FileTextOutlined />}
                            onClick={() =>
                              handleInvoiceDetails(invoices.currentInvoice)
                            }
                          >
                            {t('subscriptions.details')}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <h3>{t('subscriptions.noActiveInvoice')}</h3>
                    </div>
                  )}
                </div>
              </Card>

              <Card
                title={t('subscriptions.previousInvoices')}
                variant="borderless"
                style={{ marginTop: '10px' }}
              >
                {invoices.previousInvoices?.length ? (
                  <InvoicesTable
                    invoices={invoices.previousInvoices}
                    onViewDetails={handleInvoiceDetails}
                    onViewPayments={handlePaymentDetails}
                    onPay={(invoice) => {
                      setSelectedInvoice(invoice);
                      setOpenPayModal(true);
                    }}
                    role={loggedInUser.role.name}
                  />
                ) : (
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <h3>{t('subscriptions.noPreviousInvoice')}</h3>
                  </div>
                )}
              </Card>
            </div>
          </TabPane>
        </Tabs>
      )}

      <PayInvoiceModal
        open={openPayModal}
        onCancel={() => setOpenPayModal(false)}
        handlePayInvoice={handlePayInvoice}
      />

      <InvoiceItemsModal
        open={openItemsModal}
        onCancel={() => setOpenItemsModal(false)}
        invoiceItems={invoiceItems}
        handleDownload={handleDownloadInvoiceItems}
        handlePageChange={handleInvoiceItemsPageChange}
        currentItemsPage={currentItemsPage}
        itemsPerPage={itemsPerPage}
        itemsLoading={itemsLoading}
      />

      <SubscriptionModal
        open={openSubscriptionModal}
        onCancel={() => setOpenSubscriptionModal(false)}
        onSubmit={onSubmitSubscription}
        selectedSubscription={selectedSubscription}
        subscriptionForm={subscriptionForm}
        carHouseId={carHouseId}
        userId={userId}
        isEditSubscription={isEditSubscription}
      />

      <CreditModal
        open={openCreditModal}
        onCancel={() => setOpenCreditModal(false)}
        onSubmit={onSubmitCredit}
        selectedCarHouse={carHouse}
        creditForm={creditForm}
      />

      <PaymentDetailsModal
        open={openPaymentDetailsModal}
        onCancel={() => setOpenPaymentDetailsModal(false)}
        payments={payments}
      />
    </>
  );
};

export default SubscriptionsPage;
